import classNames from 'classnames';
import React, { ReactEventHandler, ReactNode, useState } from 'react';
import toast from 'react-hot-toast';

import { useAuth } from '../../contexts/auth';
import { useCurrentUser } from '../../util/current-user';
import { subscribeToMailchimpList } from '../../util/mailchimp';
import { Button } from '../button';
import { LoadingSpinner } from '../loading-spinner';
import {
	image as imageStyle,
	innerContainer as innerContainerStyle,
	innerContainerText as innerContainerTextStyle,
	outerContainer as outerContainerStyle,
	submitErrorMessage as submitErrorMessageStyle,
} from './styles.module.css';

export interface NewsletterHeroMailchimpProps {
	mailchimpInterestId: string;
	title?: string;
	summary?: string;
	renderImage(): ReactNode;
	callToActionTitle?: string;
	titleSubmitted?: string;
	summarySubmitted?: string;
	supportEmail?: string;
	errorMessage?: string;
}

enum FormState {
	NotSubmitted,
	Submitting,
	Submitted,
	SubmissionError,
}

export const NewsletterHeroMailchimp = ({
	mailchimpInterestId,
	title,
	summary,
	renderImage,
	callToActionTitle,
	titleSubmitted,
	summarySubmitted,
	supportEmail,
	errorMessage,
}: NewsletterHeroMailchimpProps): JSX.Element => {
	const {
		user,
		loading,
		error,
		updateMailchimpInterestSubscription,
		subscribedMailchimpInterests,
		isMailchimpMember,
		refetch,
	} = useCurrentUser();
	const { session } = useAuth();

	const [state, setState] = useState<FormState>(FormState.NotSubmitted);

	if (!renderImage) throw new Error('Missing renderImage prop to NewsletterHero');

	const handleSubmit: ReactEventHandler<HTMLFormElement> = async (event) => {
		event.preventDefault();
		try {
			setState(FormState.Submitting);
			if (!session || !user?.email) {
				console.error('Cannot subscribe to topic when not logged in');
				return;
			}
			if (!isMailchimpMember) await subscribeToMailchimpList(session, user?.email);
			await updateMailchimpInterestSubscription(mailchimpInterestId, true);
			toast.success('Subscribed to updates');
			setState(FormState.Submitted);
			await refetch();
		} catch (error) {
			setState(FormState.SubmissionError);
		}
	};

	const subscribedToThisList =
		isMailchimpMember && subscribedMailchimpInterests.some(({ id }) => mailchimpInterestId === id);

	return (
		<div className={classNames(outerContainerStyle)}>
			<div className={imageStyle}>{renderImage()}</div>
			{state === FormState.Submitted || subscribedToThisList ? (
				<div className={innerContainerStyle}>
					<div className={innerContainerTextStyle}>
						{!!titleSubmitted && <h1>{titleSubmitted}</h1>}
						{!!summarySubmitted && <p>{summarySubmitted}</p>}
					</div>
					<Button large to="/dashboard/updates">
						Manage Newsletter Updates
					</Button>
				</div>
			) : (
				<div className={innerContainerStyle}>
					<div className={innerContainerTextStyle}>
						{!!title && <h1>{title}</h1>}
						{!!summary && <p>{summary}</p>}
					</div>
					<LoadingSpinner loading={loading} error={error} data={user}>
						<form onSubmit={handleSubmit}>
							<Button large loading={state === FormState.Submitting}>
								{callToActionTitle}
							</Button>
							{state === FormState.SubmissionError && (
								<div className={submitErrorMessageStyle}>
									{errorMessage} <a href={`"mailto:${supportEmail}"`}>{supportEmail}</a>
								</div>
							)}
						</form>
					</LoadingSpinner>
				</div>
			)}
		</div>
	);
};
